'use client';

import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { tokens } from '../theme';

/* Human names for the dots. The scene keys are code identifiers; with fifteen
   slides the tooltip is what makes the dots navigable rather than decorative. */
const LABELS = {
  ManualHook: 'The manual grind',
  MeetAdzilio: 'Meet Adzilio',
  ProductMap: 'One workspace',
  Guardrails: 'Guardrails',
  Connections: 'Your ad accounts',
  Brief: 'The brief',
  PlanPreview: 'Plan preview',
  CommitGate: 'Gate 1 — spend',
  CreativeSource: 'Creative sources',
  ApprovalGate: 'Gate 2 — creative',
  LaunchPipeline: 'Launch pipeline',
  DecisionLog: 'Decision log',
  Analytics: 'Analytics',
  LeadsGate3: 'Gate 3 — outreach',
  FinalResolve: 'In your control',
};

/* Scene navigation for the film once it is looping in the hero. It polls the
   engine from its own animation frame and only calls setState when the scene
   index actually changes, so the page does not re-render at 60fps. */
export default function FilmCarousel({ controllerRef, scenes, offsets, active }) {
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (!active || !offsets || !offsets.length) return undefined;
    let raf = 0;
    const tick = () => {
      const c = controllerRef.current;
      if (c) {
        const t = c.getTime();
        let i = 0;
        for (let k = 0; k < offsets.length; k += 1) if (t >= offsets[k]) i = k;
        setIndex((prev) => (prev === i ? prev : i));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, controllerRef, offsets]);

  const go = React.useCallback(
    (i) => {
      const c = controllerRef.current;
      if (!c || !offsets || !offsets.length) return;
      const n = ((i % offsets.length) + offsets.length) % offsets.length;
      /* a hair past the boundary so the scene renders its first frame rather
         than the previous scene's last one */
      c.seek(offsets[n] + 0.001);
      c.play();
      setIndex(n);
    },
    [controllerRef, offsets],
  );

  if (!active || !scenes || !scenes.length) return null;

  const arrow = {
    color: 'rgba(237,231,255,.72)',
    width: 34,
    height: 34,
    p: 0,
    '&:hover': { background: 'rgba(255,255,255,.1)', color: '#fff' },
    '& .MuiSvgIcon-root': { fontSize: 22, display: 'block' },
  };

  return (
    <Box
      sx={{
        mt: { xs: 2, md: 2.5 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      <IconButton onClick={() => go(index - 1)} aria-label="Previous scene" sx={arrow}>
        <ChevronLeftRounded />
      </IconButton>

      <Box
        role="tablist"
        aria-label="Film scenes"
        sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}
      >
        {scenes.map((s, i) => {
          const on = i === index;
          return (
            <Tooltip key={s.name} title={LABELS[s.name] || s.name} placement="top" arrow>
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={on}
                aria-label={LABELS[s.name] || s.name}
                onClick={() => go(i)}
                sx={{
                  p: 0,
                  border: 0,
                  cursor: 'pointer',
                  height: 8,
                  width: on ? 26 : 8,
                  borderRadius: 999,
                  background: on ? tokens.grad : 'rgba(237,231,255,.26)',
                  transition: `width 320ms ${tokens.ease}, background-color 320ms ${tokens.ease}`,
                  '&:hover': { background: on ? tokens.grad : 'rgba(237,231,255,.5)' },
                  '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 3 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      <IconButton onClick={() => go(index + 1)} aria-label="Next scene" sx={arrow}>
        <ChevronRightRounded />
      </IconButton>
    </Box>
  );
}
